import { FormListItem } from "@cs-ts/form-list-item";
import generateGuid from "@/lib/generateGuid";

function buildDict(key: string, value: string) {
    return { key, value };
}
export class GQNode {
    id: string;
    vmId: string;
    type: string;
    parent: null | GQNode;

    constructor(id?: string | null) {
        this.id = id || generateGuid();
        this.vmId = generateGuid();
        this.type = "Node";
        this.parent = null;
    }
    findNode(id: string): GQNode | null {
        if (this.vmId == id || this.id == id) {
            return this;
        }
        return null;
    }
    root(): GQNode {
        if (this.parent == null) {
            return this;
        } else {
            return this.parent.root();
        }
    }
    convertToTreantStruct() {
        type TreantStruct = {
            HTMLid: string;
            HTMLclass: string;
            innerHTML: string;
            children?: TreantStruct[];
        };
        const result: TreantStruct = {
            HTMLid: this.vmId,
            HTMLclass: "ko_node",
            innerHTML: "<div />",
        };
        return result;
    }
    convertToSaveStruct(prefix: string): { key: string; value: string }[] {
        return [];
    }
    isValid() {
        return this.isThisValid();
    }
    isThisValid() {
        return true;
    }
}
export class CQActionTemplateNode extends GQNode {
    constructor() {
        super(null);
        this.type = "ActionTemplate";
    }
    isThisValid() {
        return false;
    }
}

export class CQAnswerNode extends GQNode {
    answerNext: CQActionTemplateNode;
    helpText: string | undefined;
    answerText: string | undefined;

    constructor(id?: string, answerText?: string, helpText?: string, answerNext?: CQActionTemplateNode) {
        super(id);
        this.type = "Answer";
        this.answerText = answerText;
        this.helpText = helpText;
        this.answerNext = answerNext || new CQActionTemplateNode();
        this.answerNext.parent = this as GQNode;
    }
    findNode(id: string) {
        let result = super.findNode(id);
        if (result == null && this.answerNext != null) {
            result = this.answerNext.findNode(id);
        }
        return result;
    }
    convertToTreantStruct() {
        const result = super.convertToTreantStruct();
        result.children = [this.answerNext.convertToTreantStruct()];
        return result;
    }
    convertToSaveStruct(prefix: string) {
        let result = [buildDict(prefix + ".TypeCode", "Answer"), buildDict(prefix + ".DbId", this.id), buildDict(prefix + ".AnswerText", this.answerText!), buildDict(prefix + ".HelpText", this.helpText!)];
        if (this.answerNext != null) {
            result.push(buildDict(prefix + ".Children.Index", this.answerNext.vmId));
            result = result.concat(this.answerNext.convertToSaveStruct(prefix + ".Children[" + this.answerNext.vmId + "]"));
        }
        return result;
    }
    isValid() {
        return this.isThisValid() && this.answerNext.isValid();
    }
    isThisValid() {
        return this.answerNext != null && this.answerText != null && !/^\s*$/.test(this.answerText);
    }
}

export class CQQuestionNode extends GQNode {
    tenant: string | undefined;
    questionText: string | undefined;
    answers: CQAnswerNode[];
    constructor(id?: string, tenant?: string, questionText?: string, answers?: CQAnswerNode[]) {
        super(id);
        this.tenant = tenant;
        this.type = "Question";
        this.questionText = questionText;
        this.answers = answers || [];
        for (let index = this.answers.length; index < 2; index++) {
            this.answers.push(new CQAnswerNode(null!, null!, null!, null!));
        }
        this.answers.forEach(answer => {
            answer.parent = this;
        });
    }
    findNode(id: string) {
        let result = super.findNode(id);
        if (result == null && this.answers != null) {
            this.answers.some(answer => {
                result = answer.findNode(id);
                return result != null;
            });
        }
        return result;
    }
    convertToTreantStruct() {
        const result = super.convertToTreantStruct();
        result.children = this.answers.map(i => i.convertToTreantStruct());
        return result;
    }
    convertToSaveStruct(prefix: string) {
        let result = [buildDict(prefix + ".TypeCode", "Question"), buildDict(prefix + ".DbId", this.id), buildDict(prefix + ".QuestionText", this.questionText!)];
        if (this.answers != null) {
            this.answers.forEach(answer => {
                result.push(buildDict(prefix + ".Children.Index", answer.vmId));
                result = result.concat(answer.convertToSaveStruct(prefix + ".Children[" + answer.vmId + "]"));
            });
        }
        return result;
    }
    isValid() {
        return this.isThisValid() && this.answers.every(i => i.isValid());
    }
    isThisValid() {
        return this.answers != null && this.answers.length >= 2 && this.questionText != null && !/^\s*$/.test(this.questionText);
    }
}
export class CQLinkedQuestionNode extends GQNode {
    link: CQQuestionNode | undefined;
    constructor(id?: string, link?: CQQuestionNode) {
        super(id);
        this.type = "LinkedQuestion";
        this.link = link;
    }
    convertToSaveStruct(prefix: string) {
        const result = [buildDict(prefix + ".TypeCode", "LinkedQuestion")];
        if (this.link != null) {
            result.push(buildDict(prefix + ".LinkedId", this.link.id));
            result.push(buildDict(prefix + ".Tenant", this.link.tenant!));
        }
        return result;
    }
    setLinkedQuestion(question: CQQuestionNode) {
        this.link = question;
    }
    isThisValid() {
        return this.link != null;
    }
}
export class CQFormNode extends GQNode {
    formName: string | undefined;
    formVersion: string | undefined;
    tenant: string | undefined;
    formTypes: (keyof typeof formTypeLabels)[];
    label: string | undefined;
    description: string | undefined;
    tags: string[];
    constructor(id?: string, formName?: string, formVersion?: string, tenant?: string, formTypes?: (keyof typeof formTypeLabels)[], label?: string, description?: string, tags?: string[]) {
        super(id);
        this.type = "Form";
        this.formName = formName;
        this.formVersion = formVersion;
        this.tenant = tenant;
        this.formTypes = formTypes || [];
        this.label = label;
        this.description = description;
        this.tags = tags || [];
    }
    convertToSaveStruct(prefix: string) {
        const result = [buildDict(prefix + ".TypeCode", "Form"), buildDict(prefix + ".FormTenant", this.tenant!), buildDict(prefix + ".FormName", this.formName!), buildDict(prefix + ".FormVersion", this.formVersion!)];
        return result;
    }
    setForm(form: FormListItem) {
        (["formName", "formVersion", "tenant", "formTypes", "label", "description", "tags"] as const).forEach(i => {
            //@ts-ignore
            this[i] = form[i];
        });
    }
    hasFormType(type: keyof typeof formTypeLabels) {
        if (this.formTypes == null || this.formTypes.length == 0) {
            return type == "webForm";
        }
        return this.formTypes != null && this.formTypes.includes(type);
    }
    titleAttr() {
        const typeOrders = ["webForm", "pdfForm", "link", "contact", "content"] as const;
        let mainType: keyof typeof formTypeLabels = null!;
        typeOrders.some(type => {
            const hasType = this.hasFormType(type);
            if (hasType) {
                mainType = type;
            }
            return hasType;
        });
        return this.formTypeTitleAttr(mainType);
    }
    formTypeTitleAttr(type: keyof typeof formTypeLabels) {
        return formTypeLabels[type];
    }
    isThisValid() {
        return this.formName != null && this.formVersion != null && this.tenant != null;
    }
}
const formTypeLabels = {
    webForm: "Web Form",
    pdfForm: "PDF Form",
    link: "Link",
    contact: "Contact Page",
    content: "Content Page",
};
